import { create } from 'zustand';
import {
    StudentAccommodationProfile,
    SensoryContrastMode,
} from '@eduwrite/shared';
import { useAccessibilityStore } from './useAccessibilityStore';
import { fetchWithRetry } from '../utils/fetchWithRetry';

interface AccommodationState {
    profile: StudentAccommodationProfile | null;
    isLoading: boolean;
    error: string | null;

    // Actions
    setProfile: (profile: StudentAccommodationProfile | null) => void;
    fetchAccommodations: (studentId: string, serverUrl?: string, token?: string) => Promise<StudentAccommodationProfile | null>;
    reset: () => void;

    // Computed getters
    getEffectiveTier: (activityTier?: string) => string;
    shouldAutoTts: () => boolean;
    isSpeechInputAllowed: () => boolean;
    getHesitationMultiplier: () => number;
    getSensoryContrastMode: () => SensoryContrastMode;
    shouldHideTimers: () => boolean;
}

export const useAccommodationStore = create<AccommodationState>((set, get) => ({
    profile: null,
    isLoading: false,
    error: null,

    setProfile: (profile: StudentAccommodationProfile | null) => {
        set({ profile, error: null });

        if (profile) {
            // Silently apply sensory contrast mode to accessibility store
            if (profile.sensoryContrastMode === 'high_contrast') {
                useAccessibilityStore.getState().setHighContrast(true);
            }

            // Silently apply auto TTS prompt
            if (profile.autoTtsPrompt) {
                useAccessibilityStore.getState().setLessonTextToSpeechOverride(true);
            }
        }
    },

    fetchAccommodations: async (studentId: string, serverUrl?: string, token?: string) => {
        if (!studentId) return null;
        set({ isLoading: true, error: null });

        try {
            const apiBase = (serverUrl || 'http://localhost:4005').replace(/\/+$/, '');
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const res = await fetchWithRetry(`${apiBase}/api/students/${studentId}/accommodations`, {
                method: 'GET',
                headers,
                timeoutMs: 6000,
                maxRetries: 2,
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch accommodations: ${res.status}`);
            }

            const data: StudentAccommodationProfile = await res.json();
            get().setProfile(data);
            set({ isLoading: false });
            return data;
        } catch (err: any) {
            console.warn('[useAccommodationStore] Could not load accommodations:', err.message);
            set({ isLoading: false, error: err.message });
            return null;
        }
    },

    reset: () => {
        set({ profile: null, isLoading: false, error: null });
    },

    getEffectiveTier: (activityTier?: string) => {
        const profile = get().profile;
        // If an accommodation mandates a default tier (e.g. 'support'), apply it unless the activity specifies otherwise
        if (profile?.defaultTier) {
            return profile.defaultTier;
        }
        return activityTier || 'core';
    },

    shouldAutoTts: () => {
        const profile = get().profile;
        return profile?.autoTtsPrompt === true;
    },

    isSpeechInputAllowed: () => {
        const profile = get().profile;
        return profile?.allowSpeechInput === true;
    },

    getHesitationMultiplier: () => {
        const profile = get().profile;
        return profile?.hesitationMultiplier ?? 1.0;
    },

    getSensoryContrastMode: () => {
        const profile = get().profile;
        return profile?.sensoryContrastMode ?? 'normal';
    },

    shouldHideTimers: () => {
        const profile = get().profile;
        return profile?.hideTimers === true;
    },
}));
